// The failures the Curator API declares: each refusal with its HTTP status as
// `httpApiStatus` and its authored `detail`, which the contract encodes as it
// is. Part of the Curator API contract, so it imports only effect and the
// contract's own modules. The platform's other errors, and the mapping of a
// service's failure to ServiceUnavailable or ServiceRefused, stay in
// src/domain/errors.ts. See Design > Errors, "Public error bodies".

import { Schema } from "effect";
import { InteractionView } from "./agent-activity.ts";
import { Blocker, HubId } from "./ids.ts";

// Registering a hub whose Hub ID any hub has, or whose name a hub that isn't
// archived has. The Hub ID is the primary key, so the same repository
// registers again by moving from archived to never_listed, not by inserting;
// a different repository may take an archived hub's name. See Design >
// HubRouter, "Toolsets".
export class HubAlreadyRegistered extends Schema.TaggedError<HubAlreadyRegistered>()("HubAlreadyRegistered", {
  // The hub's Hub ID.
  hub: HubId,
  detail: Schema.String,
}, { httpApiStatus: 409 }) {
  // With the detail every response for it carries.
  static of(hub: { readonly id: HubId; readonly name: string }) { return new HubAlreadyRegistered({ hub: hub.id, detail: `${hub.name} is already registered.` }); }
}

// Registering a hub whose toolset repository another hub already uses, archived
// or not. See Design > Names and definitions, "HubRepoConflict".
export class HubRepoConflict extends Schema.TaggedError<HubRepoConflict>()("HubRepoConflict", {
  repo: Schema.String,
  detail: Schema.String,
}, { httpApiStatus: 409 }) {
  // With the detail every response for it carries.
  static of(repo: string) { return new HubRepoConflict({ repo, detail: `The toolset repository ${repo} belongs to another hub.` }); }
}

// A listing change that the listing state machine doesn't allow. See Design >
// HubRouter, "Toolsets".
export class InvalidListingTransition extends Schema.TaggedError<InvalidListingTransition>()("InvalidListingTransition", {
  // The hub's Hub ID.
  hub: HubId,
  from: Schema.String,
  to: Schema.String,
  detail: Schema.String,
}, { httpApiStatus: 409 }) {
  // With the detail every response for it carries.
  static of(hub: HubId, from: string, to: string) { return new InvalidListingTransition({ hub, from, to, detail: `A hub can't go from ${from} to ${to}.` }); }
}

// A Consumer API request without a bearer token that WarmHub accepts. See
// Design > Exchange, "Bearer tokens".
export class Unauthenticated extends Schema.TaggedError<Unauthenticated>()("Unauthenticated", {
  reason: Schema.Literals(["MissingBearer", "InvalidBearer", "NoWarmHubUser"]),
  detail: Schema.String,
}, { httpApiStatus: 401 }) {}

// A request from a caller who isn't a member of OPERATOR_ORG while
// OPERATOR_ORG_ONLY is set. See Design > Operations, "Access".
export class NotOperatorOrgMember extends Schema.TaggedError<NotOperatorOrgMember>()("NotOperatorOrgMember", {
  detail: Schema.String,
}, { httpApiStatus: 403 }) {}

// A hub the caller can't see, or one that doesn't exist: the same 404 for
// both, so a hub's existence can't be probed. See Design > Exchange,
// "Curator API".
export class HubNotFound extends Schema.TaggedError<HubNotFound>()("HubNotFound", {
  detail: Schema.String,
}, { httpApiStatus: 404 }) {}

// The Curator API's refusals of a write. See Design > Exchange, "Curator API".
// An operator-only action from a curator, who can see the hub.
export class NotOperator extends Schema.TaggedError<NotOperator>()("NotOperator", {
  detail: Schema.String,
}, { httpApiStatus: 403 }) {}

// Registering a hub, or granting its WarmHub token, without repo:configure on
// its linked repository.
export class NotRepoConfigurer extends Schema.TaggedError<NotRepoConfigurer>()("NotRepoConfigurer", {
  detail: Schema.String,
}, { httpApiStatus: 403 }) {}

// An audience write the hub's linked repository can't take: `org` for a
// public repository, which every caller can read (PublicRepository), or one
// WarmHub doesn't show the curator as the hub's, or whose org's durable ID it
// didn't give them (OrgUnreadable).
// `detail` says what to do. See Design > Exchange, "Hub audiences".
export class AudienceRefused extends Schema.TaggedError<AudienceRefused>()("AudienceRefused", {
  reason: Schema.Literals(["PublicRepository", "OrgUnreadable"]),
  detail: Schema.String,
}, { httpApiStatus: 409 }) {}

// The agent path's refusals. See Design > Exchange, "The agent path".
// An operation the hub agent may not call: one off the agent path's
// allowlist, or one whose handler refuses an agent caller itself. `operation`
// is the contract's name for it.
export class AgentNotAllowed extends Schema.TaggedError<AgentNotAllowed>()("AgentNotAllowed", {
  operation: Schema.String,
  detail: Schema.String,
}, { httpApiStatus: 403 }) {}

// A call on the agent path for a hub whose Hub token can't be used: no
// curator connected the hub agent (None), its token expired (Expired), the
// daily token check refused it (Refused), or WarmHub no longer accepts it
// (Invalid). A curator of the hub connects it again.
export class HubAgentNotConnected extends Schema.TaggedError<HubAgentNotConnected>()("HubAgentNotConnected", {
  state: Schema.Literals(["None", "Expired", "Refused", "Invalid"]),
  detail: Schema.String,
}, { httpApiStatus: 403 }) {}

// A grant of the hub's WarmHub token that WarmHub or the store of hub secrets
// refused for the caller's credential: it can't read a repository the toolset
// reads, can't mint a token (an agent's, say), or sent a `token` that isn't
// the caller's or doesn't read each repository. `detail` says what to do.
export class HubTokenRefused extends Schema.TaggedError<HubTokenRefused>()("HubTokenRefused", {
  detail: Schema.String,
}, { httpApiStatus: 403 }) {}

// A commit that isn't one of the hub's toolsets.
export class ToolsetNotFound extends Schema.TaggedError<ToolsetNotFound>()("ToolsetNotFound", {
  commit: Schema.String,
  detail: Schema.String,
}, { httpApiStatus: 404 }) {
  // With the detail every response for it carries.
  static of(commit: string) { return new ToolsetNotFound({ commit, detail: `No toolset of this hub has commit ${commit}.` }); }
  // A commit the toolset repository has, off `main`: only a commit on main is a toolset.
  static offMain(commit: string) { return new ToolsetNotFound({ commit, detail: `Commit ${commit} isn't on main, and only a commit on main is a toolset.` }); }
}

// Activating, or test-running, a toolset that has blockers.
export class ActivationRefused extends Schema.TaggedError<ActivationRefused>()("ActivationRefused", {
  blockers: Schema.Array(Blocker),
  detail: Schema.String,
}, { httpApiStatus: 409 }) {
  // With the detail every response for it carries.
  static of(blockers: readonly Blocker[]) { return new ActivationRefused({ blockers, detail: "The toolset has unmet activation checks." }); }
}

// A test run of a toolset that isn't active, refused before it starts
// because the hub's WarmHub token for that toolset doesn't cover `missing`,
// its readable repositories without a grant: the HubSecretMissing blocker
// as an error. See Design > Exchange, "Test runs of a toolset".
export class HubSecretMissing extends Schema.TaggedError<HubSecretMissing>()("HubSecretMissing", {
  missing: Schema.Array(Schema.String),
  // The part of `missing` that a grant can't fix. See the blocker.
  unreadable: Schema.Array(Schema.String),
  detail: Schema.String,
}, { httpApiStatus: 409 }) {}

// A write to an archived hub, other than reading it.
export class HubArchived extends Schema.TaggedError<HubArchived>()("HubArchived", {
  // The hub's Hub ID.
  hub: HubId,
  detail: Schema.String,
}, { httpApiStatus: 409 }) {
  // With the detail every response for it carries.
  static of(hub: { readonly id: HubId; readonly name: string }) { return new HubArchived({ hub: hub.id, detail: `${hub.name} is archived.` }); }
}

// Setting an API key in a new header or query parameter for an account that
// already holds API_KEYS_PER_ACCOUNT keys, for a host past
// API_ACCOUNTS_PER_HOST accounts, or beside a grant credential. See Design >
// Exchange, "Curator API".
export class ApiKeyLimitReached extends Schema.TaggedError<ApiKeyLimitReached>()("ApiKeyLimitReached", {
  host: Schema.String,
  limit: Schema.Int,
  detail: Schema.String,
}, { httpApiStatus: 409 }) {}

// A Consumer API request over its WarmHub user's CONSUMER_LIMITER budget, a
// Curator API request over its WarmHub user's CURATOR_LIMITER budget, or a
// test run over its hub's TEST_RUNS_PER_HUB_PER_HOUR, which says when the hub
// may start one again. See Design > Exchange, "Rate limit" and "Test runs of
// a toolset".
export class RateLimited extends Schema.TaggedError<RateLimited>()("RateLimited", {
  detail: Schema.String,
  retryAfterSeconds: Schema.Finite,
}, { httpApiStatus: 429 }) {}

// A method and path that no route takes, or a test run the hub doesn't have.
// See Design > Errors, "An unmatched route is a 404".
export class NotFound extends Schema.TaggedError<NotFound>()("NotFound", {
  detail: Schema.String,
}, { httpApiStatus: 404 }) {}

// An activation that met a concurrent change to the toolset or its hub, such
// as another activation or an archive, after its one retry. Reading the
// toolset again and retrying may succeed. See Design > Exchange,
// "Activation".
export class ToolsetChanged extends Schema.TaggedError<ToolsetChanged>()("ToolsetChanged", {
  // The hub's Hub ID.
  hub: HubId,
  commit: Schema.String,
  detail: Schema.String,
}, { httpApiStatus: 409 }) {}

// A Curator reply to an Interaction that is no longer open: answered with a
// different reply, expired, superseded or cancelled. `interaction` is the
// Interaction as it is, whose state says which won. See Design > Exchange,
// "The hub agent's activity".
export class InteractionClosed extends Schema.TaggedError<InteractionClosed>()("InteractionClosed", {
  interaction: InteractionView,
  detail: Schema.String,
}, { httpApiStatus: 409 }) {
  // With the detail every response for it carries, in words for the state that closed it.
  static of(interaction: InteractionView) { return new InteractionClosed({ interaction, detail: CLOSED_WORDS[interaction.state] }); }
}
const CLOSED_WORDS = {
  open: "This is still open.",
  answered: "This already has a reply, and the hub agent keeps only the first.",
  expired: "This expired before your reply, so the hub agent took nothing from it.",
  superseded: "Your hub agent replaced this with a newer one; reply there instead.",
  cancelled: "This was withdrawn, so it takes no reply.",
} as const satisfies Record<InteractionView["state"], string>;

// A Curator reply its Interaction's reply schema refuses, such as a key that
// isn't one of its options or a revise without a note. It decoded, but
// conflicts with what the stored Interaction accepts, so it is a 409, as
// InvalidListingTransition is; a 400 on the Curator API is only a request
// that didn't decode (Design > Errors, "HTTP status"). `detail` is
// checkReply's words (src/contract/interaction.ts), which are ours.
export class ReplyInvalid extends Schema.TaggedError<ReplyInvalid>()("ReplyInvalid", {
  detail: Schema.String,
}, { httpApiStatus: 409 }) {}

// The intent index rebuild refused, leaving the index as it was: D1 lists no
// hub that isn't archived, a live hub's toolset doesn't decode, or no toolset
// has rows to write. See Design > Operations, "Intent index rebuild".
export class IntentIndexRebuildRefused extends Schema.TaggedError<IntentIndexRebuildRefused>()("IntentIndexRebuildRefused", {
  reason: Schema.Literals(["NoLiveHub", "UndecodableToolset", "NoIndexRows"]),
  detail: Schema.String,
}, { httpApiStatus: 409 }) {}

// The Curator API's public form of a service's failure, which keeps its
// cause, operation and status to the log line: an outage, or a binding this
// deployment lacks, is ServiceUnavailable; a definite refusal, or an answer
// the platform can't use, is ServiceRefused. Each carries authored `detail`
// and WarmHub's `code` when it sent one. See Design > Errors, "An outage is a
// 503" and "Redaction".
export class ServiceUnavailable extends Schema.TaggedError<ServiceUnavailable>()("ServiceUnavailable", {
  detail: Schema.String,
  code: Schema.optionalKey(Schema.String),
  // NotConfigured: this environment lacks the binding or setting the
  // request needs, such as the hub agent outside dev, so trying again won't
  // help. Without it, the outage may pass. See Design > Errors.
  reason: Schema.optionalKey(Schema.Literal("NotConfigured")),
  retryAfterSeconds: Schema.optionalKey(Schema.Finite),
}, { httpApiStatus: 503 }) {}

export class ServiceRefused extends Schema.TaggedError<ServiceRefused>()("ServiceRefused", {
  detail: Schema.String,
  code: Schema.optionalKey(Schema.String),
}, { httpApiStatus: 502 }) {}
